/**
 * Pleasur.ai Brand Configuration
 * ================================
 * Central source of truth for all brand colors and tokens.
 * Reference this file instead of hardcoding hex values in components.
 *
 * Active Sora-aligned system (use these for new code):
 *   canvas    — page-level dark/light backgrounds
 *   surfaces  — glass overlays + subtle elevation washes
 *   borders   — hairline-only (no colored borders)
 *   text      — opacity-driven hierarchy on a single white/near-black token
 *   accents   — Pleasur-blue + heart + success/danger/warning (used sparingly)
 *   motion    — durations, easings, spring presets (Sora cadence)
 *   elevation — "no shadow" doctrine encoded as tokens
 *   radius    — Sora radius scale
 *   blur      — backdrop-blur scale
 *
 * Legacy back-compat (do NOT use in new code — marked @deprecated):
 *   primaries (blue, coral)  — consolidated under `accents.blue` + `legacy.coral`
 *   moods (all 8)            — Sora rebrand removes the multi-color palette
 *   colors.{primary,coral,*} — same retirement
 *   gradients.*              — Sora design language bans decorative gradients
 *
 * See: .claude/rules/brand.md §7
 */

export const brand = {
  // ── The 4 Primaries ──
  // These define Pleasur.ai's visual identity at the highest level.
  primaries: {
    /** True Black — The canvas, depth, grounding */
    black: "#000000",
    /** Pure White — Clarity, primary text on dark */
    white: "#FFFFFF",
    /** @deprecated Use `brand.accents.blue` — same hex `#1d7df2`, consolidated location. */
    blue: "#1d7df2",
    /** @deprecated Coral retired from chrome. Use `brand.accents.heart` (#FF4D6D) for likes,
     *  or `brand.legacy.coral` for one-off premium opt-ins. */
    coral: "#FF6B9D",
  },

  // ── The 8 Moods ──
  /** @deprecated The entire mood palette is retired in the Sora rebrand —
   *  Sora's design language uses a single accent, not a multi-color brand
   *  palette. Use `brand.accents.*` instead. See brand.md §3 + §7.
   *  Existing usages migrate in Phases 2–6; the block is removed in Phase 7. */
  moods: {
    /** Pulse — Electric cyan. Energy, highlights, active states, live indicators */
    pulse: "#3AF2FF",
    /** Desire — Deep rose. Passion, hearts, intimate features, premium */
    desire: "#E8527F",
    /** Aura — Rich violet. AI generation, magic moments, creative tools */
    aura: "#7C3AED",
    /** Reverie — Soft lavender. Dreams, AI imagination, gentle accents */
    reverie: "#C4A8FF",
    /** Nectar — Warm amber. Credits, rewards, achievements, notifications */
    nectar: "#F59E0B",
    /** Whisper — Soft mint. Calm states, subtle highlights, fresh contexts */
    whisper: "#B8F0E8",
    /** Velvet — Deep plum. Luxury, depth, premium tier, dark accents */
    velvet: "#3D0A2E",
    /** Dusk — Warm sand. Warmth, soft backgrounds, onboarding, comfort */
    dusk: "#E8D5B7",
  },

  colors: {
    // ── Primary shades ──
    /** @deprecated Use `brand.accents.blue` — same hex `#1d7df2`, consolidated. */
    primary: "#1d7df2",
    /** @deprecated Pleasur-blue dark variant retired. */
    primaryDark: "#1563c4",
    /** @deprecated Coral retired from chrome. Use `brand.accents.heart` or `brand.legacy.coral`. */
    coral: "#FF6B9D",
    /** @deprecated Coral dark variant retired. */
    coralDark: "#E8527F",

    // ── Foundations ──
    /** Deep Onyx — Card surfaces */
    onyx: "#0d1117",
    /** True black background */
    onyxDeep: "#000000",

    // ── Text ──
    /** Dusty Blue — Muted text, secondary elements */
    dusty: "#66757F",
    /** Mist — Light borders, subtle backgrounds */
    mist: "#CCD6DD",
    /** Mystic Glow — Lightest surfaces */
    mystic: "#E1E8ED",
    /** Full White — Text on dark surfaces */
    white: "#FFFFFF",

    // ── Semantic ──
    /** Destructive red — Errors, danger, delete actions */
    destructive: "#ef4444",
    /** Success green — Confirmations, completed states */
    success: "#22c55e",
  },

  /** @deprecated The entire `gradients` block is retired in the Sora rebrand.
   *  Sora's design language bans decorative gradients (see .claude/rules/brand.md
   *  §3 + §7). Existing usages unhook in Phase 6; block removed in Phase 7.
   *  For card elevation, use `surface-subtle` / `surfaces.subtleDark` instead. */
  gradients: {
    /** @deprecated Use solid `accents.blue` or no fill. */
    primary: "linear-gradient(135deg, #1d7df2, #1563c4)",
    /** Coral gradient: Warm engagement contexts */
    coral: "linear-gradient(135deg, #FF6B9D, #E8527F)",
    /** Duo gradient: Blue → Coral (hero, premium — the brand in motion) */
    duo: "linear-gradient(135deg, #1d7df2, #FF6B9D)",
    /** Warm gradient: Coral → Violet (AI creative, generation) */
    warm: "linear-gradient(135deg, #FF6B9D, #7C3AED)",
    /** Glow gradient: Violet → Blue (deep features, pro) */
    glow: "linear-gradient(135deg, #7C3AED, #1d7df2)",
    /** Pulse gradient: Cyan → Blue (tech, energy, live) */
    pulse: "linear-gradient(135deg, #3AF2FF, #1d7df2)",
    /** Sunset gradient: Coral → Amber (warmth, rewards) */
    sunset: "linear-gradient(135deg, #FF6B9D, #F59E0B)",
    /** Subtle gradient for cards */
    card: "linear-gradient(135deg, #0d1117, #111820)",
  },

  // ─────────────────────────────────────────────────────────────
  // Sora-inspired token system (preview — Phase 1)
  // Restraint over decoration. Black canvas, hairline structure,
  // glass overlays, opacity-driven hierarchy. Use these tokens for
  // new UI; the keys above remain for back-compat only.
  // ─────────────────────────────────────────────────────────────

  /** Canvas — the absolute background for each theme */
  canvas: {
    /** Pure black — the app */
    dark: "#000000",
    /** Pure white — auth, marketing */
    light: "#FFFFFF",
  },

  /** Surfaces — frosted glass for modals/popovers, subtle elevation for cards */
  surfaces: {
    /** Glass overlay on dark — pair with backdrop-blur(48px) */
    glassDark: "rgba(20, 20, 22, 0.72)",
    /** Glass overlay on light — pair with backdrop-blur(48px) */
    glassLight: "rgba(255, 255, 255, 0.85)",
    /** Subtle elevated surface on dark canvas (cards) */
    subtleDark: "rgba(255, 255, 255, 0.03)",
    /** Slightly stronger subtle surface (hovered cards, selected nav) */
    subtleDarkStrong: "rgba(255, 255, 255, 0.06)",
  },

  /** Borders — hairline, opacity-driven, never solid */
  borders: {
    /** Default hairline on dark */
    hairlineDark: "rgba(255, 255, 255, 0.08)",
    /** Hover/focus hairline on dark */
    hairlineDarkStrong: "rgba(255, 255, 255, 0.14)",
    /** Default hairline on light */
    hairlineLight: "rgba(0, 0, 0, 0.08)",
    /** Hover/focus hairline on light */
    hairlineLightStrong: "rgba(0, 0, 0, 0.14)",
  },

  /** Text — opacity-based hierarchy, not separate colors */
  text: {
    onDark: {
      primary: "#FFFFFF",
      secondary: "rgba(255, 255, 255, 0.62)",
      tertiary: "rgba(255, 255, 255, 0.42)",
      disabled: "rgba(255, 255, 255, 0.28)",
    },
    onLight: {
      primary: "#0A0A0A",
      secondary: "rgba(10, 10, 10, 0.62)",
      tertiary: "rgba(10, 10, 10, 0.42)",
      disabled: "rgba(10, 10, 10, 0.28)",
    },
  },

  /** Accents — used sparingly. No decorative gradients. */
  accents: {
    /** Pleasur-blue — links, focus rings, selection, brand-identity moments.
     *  Owner decision (2026-05-24): kept Pleasur-blue over Sora's quieter #5C7CFA
     *  to preserve brand recognition. Use sparingly to avoid "too much blue." */
    blue: "#1d7df2",
    /** Heart — likes, premium moments only */
    heart: "#FF4D6D",
    /** System feedback */
    success: "#22C55E",
    danger: "#EF4444",
    warning: "#F59E0B",
  },

  /** Motion — Sora cadence: slow, weighted, deferred.
   *  Use Framer Motion variants with `ease: brand.motion.easing.standard`. */
  motion: {
    /** Durations in milliseconds. */
    duration: {
      /** Hover/focus/press state changes. */
      fast: 150,
      /** Default UI transitions (open/close, expand/collapse). */
      base: 220,
      /** Layout transitions (panel resize, reorder). */
      slow: 400,
      /** Hero/scene reveals. */
      cinematic: 1100,
    },
    /** Easing curves. `standard` is the Sora house curve. */
    easing: {
      /** cubic-bezier(0.22, 1, 0.36, 1) — quiet ease-out. House standard. */
      standard: [0.22, 1, 0.36, 1] as const,
      /** Quick acceleration in (button press, panel slide-in). */
      accelerate: [0.4, 0, 1, 1] as const,
      /** Soft deceleration out (panel slide-out, fade-to-empty). */
      decelerate: [0, 0, 0.2, 1] as const,
    },
    /** Spring presets — never overshoot. Use sparingly. */
    spring: {
      /** Tight: status badges, switch flips. */
      tight: { stiffness: 300, damping: 32 },
      /** Standard: panels, popovers. */
      standard: { stiffness: 240, damping: 30 },
      /** Soft: hero reveals, large layout transitions. */
      soft: { stiffness: 180, damping: 28 },
    },
  },

  /** Elevation — Sora doctrine: no drop shadows. Elevation comes from
   *  surface wash + hairline border. Token-encoded so the doctrine is enforced. */
  elevation: {
    none: "none",
    /** Subtle wash + hairline. Use `.surface-subtle` or `surfaces.subtleDark`. */
    subtle: "subtle-wash",
    /** Glass overlay for modal/popover. Use `.glass-panel`. */
    glass: "glass-panel",
  },

  /** Radius — Sora scale. Standardize on these six values. */
  radius: {
    pill: 9999,
    xs: 8,
    sm: 12,
    md: 16,
    lg: 20,
    xl: 24,
  },

  /** Blur — backdrop-blur scale (px). 48 is Sora glass-panel default. */
  blur: {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 24,
    xl: 48,
  },

  /** Legacy — explicit opt-in slot for retired colors. Use only when a one-off
   *  premium moment genuinely needs the legacy coral and the team approves it. */
  legacy: {
    /** Original Pleasur coral. Survives only for explicit premium opt-ins. */
    coral: "#FF6B9D",
  },
} as const
